import { object, string, array } from "yup";
import { addressLookupSchema } from "@shared-kernel/primary/forms/validations/postal-address.schema";
import type { DateScheduleConfig } from "@academy-context/primary/shared/trainings/training-schedule-form/shared/training-schedule-form.types";
import {
  convertFormSchedulesToApiSchedules,
  convertApiSchedulesToFormSchedules,
} from "@academy-context/primary/shared/trainings/training-schedule-form/shared/training-schedule-form.validation";
import type { Attachment, ATTACHMENT_LABEL } from "@academy-context/shared/external-provider-batch/types/provider-training-form.types";
import {
  providerInformationSchema,
  trainingDetailsSchema,
} from "@academy-context/shared/external-provider-batch/form-validation/shared-provider-training.schema";
import type {
  ExternalProviderDashboardVM,
  SubmitRequestDataBody,
} from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import { postalAddressToAddressLookup } from "@shared-kernel/domain/types/postal-address";
import { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import { formatActivityRegistrationNumber, formatSiret, normalizeIdentifier, normalizeSiret } from "@utils/formatting";

export interface SpeakerFormInput {
  firstName: string;
  lastName: string;
  resumeUrl: string;
}

export interface RequestDataFormInputs {
  // Provider fields
  providerName: string;
  providerContactFirstName: string;
  providerContactLastName: string;
  providerEmail: string;
  providerPhone: string;
  providerSiret: string;
  providerCountry: SUPPORTED_COUNTRY;
  providerActivityRegistrationNumber: string;

  // Training fields
  title: string;
  description: string;
  objective: string;
  price: number | "";
  websiteUrl: string;
  trainingSchedules: DateScheduleConfig[];
  attachments: Attachment[];

  // New required provider fields
  legalRepresentativeFirstName: string;
  legalRepresentativeLastName: string;
  trainingLocation: AddressLookup;
  trainingAddressComplement: string;
  trainingCountry: SUPPORTED_COUNTRY;
  equipmentDescription: string;
  speakers: SpeakerFormInput[];
}

export const defaultRequestDataValues: RequestDataFormInputs = {
  providerName: "",
  providerContactFirstName: "",
  providerContactLastName: "",
  providerEmail: "",
  providerPhone: "",
  providerSiret: "",
  providerCountry: SUPPORTED_COUNTRY.FRANCE,
  providerActivityRegistrationNumber: "",
  title: "",
  description: "",
  objective: "",
  price: "",
  websiteUrl: "",
  trainingSchedules: [],
  attachments: [],
  legalRepresentativeFirstName: "",
  legalRepresentativeLastName: "",
  trainingLocation: {
    placeId: "",
    description: "",
    sessionToken: "",
    invalid: false,
  },
  trainingAddressComplement: "",
  trainingCountry: SUPPORTED_COUNTRY.FRANCE,
  equipmentDescription: "",
  speakers: [{ firstName: "", lastName: "", resumeUrl: "" }],
};

const speakerSchema = object().shape({
  firstName: string().required("Le prénom est requis").max(100, "Maximum 100 caractères"),
  lastName: string().required("Le nom est requis").max(100, "Maximum 100 caractères"),
  resumeUrl: string(), // Optional
});

export const requestDataFormSchema = object().shape({
  // Shared provider information fields
  ...providerInformationSchema,

  // Shared training details fields
  ...trainingDetailsSchema,

  // Provider-specific fields
  legalRepresentativeFirstName: string().required("Le prénom du représentant légal est requis").max(100, "Maximum 100 caractères"),
  legalRepresentativeLastName: string().required("Le nom du représentant légal est requis").max(100, "Maximum 100 caractères"),
  trainingLocation: addressLookupSchema,
  trainingAddressComplement: string().max(255).defined(),
  trainingCountry: string().required("Le pays est requis"),
  equipmentDescription: string().required("La description des moyens techniques est requise"),
  speakers: array()
    .of(speakerSchema)
    .transform((value, original) => (original == null ? [] : value))
    .min(1, "Au moins un intervenant est requis"),
});

export const formatDashboardDataToFormData = (data: ExternalProviderDashboardVM): RequestDataFormInputs => {
  return {
    providerName: data.provider.name,
    providerContactFirstName: data.provider.contact.firstName,
    providerContactLastName: data.provider.contact.lastName,
    providerEmail: data.provider.email,
    providerPhone: data.provider.phone,
    providerSiret: data.provider.siret ? formatSiret(data.provider.siret) : defaultRequestDataValues.providerSiret,
    providerCountry: data.provider.country ?? defaultRequestDataValues.providerCountry,
    providerActivityRegistrationNumber: data.provider.activityRegistrationNumber
      ? formatActivityRegistrationNumber(data.provider.activityRegistrationNumber)
      : defaultRequestDataValues.providerActivityRegistrationNumber,
    title: data.training.title,
    description: data.training.description,
    objective: data.training.objective,
    price: data.training.price,
    websiteUrl: data.training.websiteUrl ?? defaultRequestDataValues.websiteUrl,
    trainingSchedules: convertApiSchedulesToFormSchedules(data.training.trainingSchedules),
    attachments: data.training.attachments.map(att => ({ url: att.url, label: att.label })),
    trainingLocation: data.training.trainingLocation
      ? postalAddressToAddressLookup(data.training.trainingLocation)
      : defaultRequestDataValues.trainingLocation,
    trainingAddressComplement: data.training.trainingLocation?.addressComplement ?? defaultRequestDataValues.trainingAddressComplement,
    trainingCountry: data.training.trainingLocation?.country || defaultRequestDataValues.trainingCountry,
    equipmentDescription: data.training.equipmentDescription ?? defaultRequestDataValues.equipmentDescription,
    legalRepresentativeFirstName: data.provider.legalRepresentative?.firstName ?? defaultRequestDataValues.legalRepresentativeFirstName,
    legalRepresentativeLastName: data.provider.legalRepresentative?.lastName ?? defaultRequestDataValues.legalRepresentativeLastName,
    speakers:
      data.training.speakers && data.training.speakers.length > 0
        ? data.training.speakers.map(speaker => ({
            firstName: speaker.firstName,
            lastName: speaker.lastName,
            resumeUrl: speaker.resumeUrl ?? "",
          }))
        : defaultRequestDataValues.speakers,
  };
};

export const formatFormDataToBody = (formData: RequestDataFormInputs): SubmitRequestDataBody => {
  return {
    // Provider fields
    providerName: formData.providerName,
    providerContactFirstName: formData.providerContactFirstName,
    providerContactLastName: formData.providerContactLastName,
    providerEmail: formData.providerEmail,
    providerPhone: formData.providerPhone,
    providerSiret: normalizeSiret(formData.providerSiret, formData.providerCountry),
    providerCountry: formData.providerCountry,
    providerActivityRegistrationNumber: normalizeIdentifier(formData.providerActivityRegistrationNumber) || null,
    // Training fields
    title: formData.title,
    description: formData.description,
    objective: formData.objective,
    price: formData.price as number,
    trainingSchedules: convertFormSchedulesToApiSchedules(formData.trainingSchedules),
    attachments: formData.attachments.filter((att): att is { url: string; label: ATTACHMENT_LABEL } => att.label !== ""),
    websiteUrl: formData.websiteUrl || null,
    // Pure provider fields
    legalRepresentativeFirstName: formData.legalRepresentativeFirstName,
    legalRepresentativeLastName: formData.legalRepresentativeLastName,
    trainingLocation: formData.trainingLocation,
    trainingAddressComplement: formData.trainingAddressComplement || null,
    equipmentDescription: formData.equipmentDescription,
    speakers: formData.speakers.map(speaker => ({
      firstName: speaker.firstName,
      lastName: speaker.lastName,
      resumeUrl: speaker.resumeUrl || null,
    })),
  };
};
